import React, { useState, createElement } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const GenericSheetMusicViewer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    id
  } = useParams();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100); // Get item data from location state
  const item = location.state?.item;
  // If no item data, redirect back to library
  // Check if user came from teacher or student route
  if (!item) {
    const referrer = document.referrer;
    const redirectPath = referrer.includes('teacher-sheet-music') ? '/teacher-sheet-music' : '/sheet-music';
    navigate(redirectPath);
    return null;
  }
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    const resolvedUrl = item.firebaseUrl ?? item.pdfUrl;
    const link = document.createElement('a');
    link.href = resolvedUrl;
    link.download = `${item.composer} - ${item.title}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-3953">
      <Navbar data-id="element-3954" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-3955">
        {/* Hero Banner */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-3956">
          {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt={item.title} className="w-full h-64 md:h-80 object-cover" data-id="element-3957" /> : <div className={`w-full h-64 md:h-80 ${isDark ? 'bg-navy-900' : 'bg-gray-200'}`} data-id="element-3958" />}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-3959" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-3960">
            <div className="max-w-7xl mx-auto" data-id="element-3961">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-3962">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-3963" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-3964">
                {item.title}
              </h1>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-3965">
                {item.composer}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-3966">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-3967">
          <div className="flex items-center space-x-2" data-id="element-3968">
            <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-3969">
              <ZoomOutIcon className="h-5 w-5" data-id="element-3970" />
            </button>
            <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3971">
              {zoom}%
            </span>
            <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-3972">
              <ZoomInIcon className="h-5 w-5" data-id="element-3973" />
            </button>
          </div>
          <div className="flex items-center space-x-2" data-id="element-3974">
            <button onClick={handlePrint} className={`flex items-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-3975">
              <PrinterIcon className="h-4 w-4 mr-2" data-id="element-3976" />
              <span className="hidden sm:inline" data-id="element-3977">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-3978">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-3979" />
              <span className="hidden sm:inline" data-id="element-3980">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-3981">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-3982">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-3983">
            <iframe src={`${item.firebaseUrl ?? item.pdfUrl}#view=FitH`} className="w-full h-full" title={`${item.title} Sheet Music`} style={{
            border: 'none',
            transform: `scale(${zoom / 100})`,
            transformOrigin: 'top center',
            transition: 'transform 0.2s ease'
          }} data-id="element-3984" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-3985">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3986">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-3987">
            <div data-id="element-3988">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3989">
                Details
              </h3>
              <dl className="space-y-2" data-id="element-3990">
                <div data-id="element-3991">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3992">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3993">
                    {item.composer}
                  </dd>
                </div>
                <div data-id="element-3994">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3995">
                    Era
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3996">
                    {item.era}
                  </dd>
                </div>
                <div data-id="element-3997">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3998">
                    Difficulty
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3999">
                    {item.difficulty}
                  </dd>
                </div>
                <div data-id="element-4000">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4001">
                    Instruments
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4002">
                    {item.instruments.join(', ')}
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-4003">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4004">
                Tags
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-4005">
                {item.tags.map((tag: string, index: number) => <span key={index} className={`text-sm px-3 py-1 rounded-full ${isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-50 text-blue-600'}`} data-id="element-4006">
                    {tag}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-4007" />
    </div>;
};